import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  Stack,
  Typography,
  Alert,
  Button,
  Chip,
  ButtonBase,
  Slide,
  TextField,
  Tooltip,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import MapIcon from "@mui/icons-material/Map";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

import { DATES, EVENT_TITLE } from "../config";
import { buildSpotLayout, spotInitials, describeAssignment } from "../spots";
import { buildDayPool, poolRemaining, buildUnitLookup } from "../bookings";
import { fetchConfirmations } from "../adminData";
import {
  watchSpotAssignments,
  assignSpot,
  unassignSpot,
  setSpotAlias,
  moveSpot,
} from "../assignments";
import { database as db } from "../firebaseConfig";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const dayLabel = (dateString, options) =>
  new Intl.DateTimeFormat("en-US", { timeZone: "UTC", ...options }).format(
    new Date(dateString),
  );

const spotSx = (size, { assigned, aliasOnly, isTarget }) => ({
  width: size,
  height: size,
  borderRadius: 1.5,
  fontSize: size < 24 ? 9 : 11,
  fontWeight: 700,
  transition: "all 120ms ease",
  ...(assigned
    ? {
        bgcolor: "primary.main",
        color: "common.white",
        "&:hover": { bgcolor: "primary.dark" },
      }
    : {
        bgcolor: "background.paper",
        color: aliasOnly ? "secondary.main" : "text.disabled",
        border: "1.5px dashed",
        borderColor: aliasOnly ? "secondary.light" : "grey.400",
        "&:hover": { bgcolor: "#e3f1f4" },
      }),
  ...(isTarget && {
    borderStyle: "solid",
    borderColor: "secondary.main",
    boxShadow: "0 0 0 3px rgba(238, 111, 87, 0.25)",
  }),
});

const LegendSwatch = ({ sx, label }) => (
  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
    <Box sx={{ width: 12, height: 12, borderRadius: 0.5, ...sx }} />
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Box>
);

const SpotMapDialog = ({ open, onClose }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const small = useMediaQuery(theme.breakpoints.down("sm"));

  const [dayIndex, setDayIndex] = React.useState(0);
  const [confirmations, setConfirmations] = React.useState([]);
  const [assignments, setAssignments] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [armedKey, setArmedKey] = React.useState(null);
  const [movingSpot, setMovingSpot] = React.useState(null);
  const [editingSpot, setEditingSpot] = React.useState(null);
  const [aliasDraft, setAliasDraft] = React.useState("");

  const date = DATES[dayIndex];
  const dayAssignments = React.useMemo(
    () => assignments?.[date] || {},
    [assignments, date],
  );
  const size = small ? 20 : 30;
  const layout = buildSpotLayout(date);

  React.useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    fetchConfirmations(db)
      .then((all) => {
        if (!cancelled) setConfirmations(all);
      })
      .catch(() => {
        if (!cancelled) setError("Couldn't load bookings.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  React.useEffect(() => {
    if (!open) return undefined;
    return watchSpotAssignments(db, (value) => setAssignments(value || {}));
  }, [open]);

  const pool = React.useMemo(
    () => buildDayPool(confirmations, date),
    [confirmations, date],
  );
  const unplaced = React.useMemo(
    () => poolRemaining(pool, dayAssignments),
    [pool, dayAssignments],
  );
  const unplacedCount = unplaced.reduce((sum, entry) => sum + entry.remaining, 0);
  const placedCount = Object.values(dayAssignments).filter((a) => a?.key).length;
  const armedEntry = unplaced.find((entry) => entry.key === armedKey) || null;
  const bookedCount = pool.reduce((sum, entry) => sum + entry.total, 0);
  const overflow = bookedCount - layout.total;

  const handleDownloadPdf = async () => {
    // jsPDF is heavy, so it loads only when a report is requested.
    const { downloadSpotMapPdf } = await import("../spotReport");
    downloadSpotMapPdf(assignments, buildUnitLookup(confirmations));
  };

  React.useEffect(() => {
    // Clear a stale arming when switching days or when the person's
    // last set has just been placed.
    if (armedKey && !unplaced.some((entry) => entry.key === armedKey)) {
      setArmedKey(null);
    }
  }, [armedKey, unplaced]);

  React.useEffect(() => {
    // Don't carry a set picked up on one day over to another.
    setMovingSpot(null);
  }, [date]);

  const placeAt = async (spot, entry) => {
    if (!entry || dayAssignments[spot]?.key) return;
    setArmedKey(null);
    try {
      await assignSpot(db, date, spot, { key: entry.key, bookedBy: entry.name });
    } catch (err) {
      setError("Couldn't save that assignment.");
    }
  };

  const moveTo = async (fromSpot, toSpot) => {
    setMovingSpot(null);
    if (fromSpot === toSpot) return;
    try {
      await moveSpot(db, date, fromSpot, toSpot, dayAssignments);
    } catch (err) {
      setError("Couldn't move that set.");
    }
  };

  const handleSpotClick = (spot) => {
    // A set picked up for rearranging drops onto whatever you tap next;
    // tapping it again puts it back down.
    if (movingSpot != null) return moveTo(movingSpot, spot);
    if (armedEntry && !dayAssignments[spot]?.key) return placeAt(spot, armedEntry);
    setEditingSpot(spot);
    setAliasDraft(dayAssignments[spot]?.alias || "");
  };

  const handleDragStart = (event, spot) => {
    event.dataTransfer.setData("text/plain", `spot:${spot}`);
    setArmedKey(null);
    setMovingSpot(spot);
  };

  const handleDrop = (event, spot) => {
    event.preventDefault();
    const payload = event.dataTransfer.getData("text/plain");
    if (payload.startsWith("spot:")) {
      return moveTo(Number(payload.slice(5)), spot);
    }
    const entry = unplaced.find((candidate) => candidate.key === payload);
    if (entry) placeAt(spot, entry);
  };

  const closeEditor = () => setEditingSpot(null);

  const saveAlias = async () => {
    try {
      await setSpotAlias(db, date, editingSpot, aliasDraft);
    } catch (err) {
      setError("Couldn't save that name.");
    }
    closeEditor();
  };

  // Touch has no drag, so the editor can pick a set up instead.
  const handleMoveFromEditor = () => {
    setArmedKey(null);
    setMovingSpot(editingSpot);
    closeEditor();
  };

  const handleUnassign = async () => {
    try {
      // Save any pending alias edit first — unassigning keeps the alias.
      if ((dayAssignments[editingSpot]?.alias || "") !== aliasDraft.trim()) {
        await setSpotAlias(db, date, editingSpot, aliasDraft);
      }
      await unassignSpot(db, date, editingSpot);
    } catch (err) {
      setError("Couldn't unassign that spot.");
    }
    closeEditor();
  };

  const renderSpot = (spot) => {
    const assignment = dayAssignments[spot];
    const assigned = Boolean(assignment?.key);
    const label = describeAssignment(assignment);
    const lifted = movingSpot === spot;
    const tile = (
      <ButtonBase
        draggable={assigned}
        onDragStart={
          assigned ? (event) => handleDragStart(event, spot) : undefined
        }
        onDragEnd={() => setMovingSpot(null)}
        onClick={() => handleSpotClick(spot)}
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => handleDrop(event, spot)}
        sx={{
          ...spotSx(size, {
            assigned,
            aliasOnly: !assigned && Boolean(assignment?.alias),
            // While a set is in hand every other spot is a target —
            // an occupied one trades places with it.
            isTarget:
              (Boolean(armedEntry) && !assigned) ||
              (movingSpot != null && !lifted),
          }),
          ...(assigned && { cursor: "grab" }),
          ...(lifted && { opacity: 0.45, transform: "scale(0.92)" }),
        }}
      >
        {assigned || assignment?.alias ? spotInitials(assignment) : spot}
      </ButtonBase>
    );
    return (
      <Box key={spot} sx={{ display: "inline-flex" }}>
        {label ? (
          <Tooltip title={`#${spot} — ${label}`} placement="top">
            {tile}
          </Tooltip>
        ) : (
          tile
        )}
      </Box>
    );
  };

  const editingAssignment = editingSpot != null ? dayAssignments[editingSpot] : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="md"
      TransitionComponent={Transition}
    >
      <Box
        sx={{
          position: "relative",
          px: 3,
          py: 2.5,
          color: "common.white",
          textAlign: "center",
          background:
            "linear-gradient(115deg, #004a58 0%, #00687b 55%, #00829a 100%)",
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            color: "rgba(255,255,255,0.8)",
          }}
        >
          <CloseIcon />
        </IconButton>
        <Box
          sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1 }}
        >
          <MapIcon fontSize="small" />
          <Typography variant="h6">Beach Spot Map</Typography>
        </Box>
        <Typography variant="body2" sx={{ opacity: 0.8 }}>
          {EVENT_TITLE} · Tops'l Beach
        </Typography>
      </Box>

      <DialogContent sx={{ px: { xs: 1.5, sm: 3 }, py: 2.5 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        <Box
          sx={{ display: "flex", gap: 1, justifyContent: "center", flexWrap: "wrap" }}
        >
          {DATES.map((eventDate, i) => {
            const placed = Object.values(assignments?.[eventDate] || {}).filter(
              (a) => a?.key,
            ).length;
            const label = `${dayLabel(eventDate, { weekday: "short" })} ${dayLabel(
              eventDate,
              { day: "numeric" },
            )}`;
            return (
              <Chip
                key={eventDate}
                label={placed ? `${label} · ${placed}` : label}
                color={i === dayIndex ? "primary" : "default"}
                variant={i === dayIndex ? "filled" : "outlined"}
                onClick={() => setDayIndex(i)}
              />
            );
          })}
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", textAlign: "center", mt: 0.5, mb: 1.5 }}
        >
          {placedCount} of {layout.total} placed · {unplacedCount} set
          {unplacedCount === 1 ? "" : "s"} to place ·{" "}
          {dayLabel(DATES[dayIndex], {
            weekday: "long",
            month: "long",
            day: "numeric",
          })}
        </Typography>

        {overflow > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {bookedCount} sets booked but this day's chart only has {layout.total}{" "}
            positions — {overflow} can't be placed. Check the layout with the beach
            service.
          </Alert>
        )}
        {!layout.confirmed && (
          <Alert severity="info" sx={{ mb: 2 }}>
            This day's layout hasn't been confirmed against the beach service's
            chart yet, so the numbering may not match their tags.
          </Alert>
        )}

        {/* Drawn the way the beach service charts it: a box opening
            toward the water on the left, numbered along the top, down
            the right column, then back along the bottom. */}
        <Box
          sx={{
            bgcolor: "#fdf6ec",
            border: "1px solid #eadfc8",
            borderRadius: 3,
            p: { xs: 1, sm: 2 },
            display: "flex",
            gap: { xs: 0.5, sm: 1 },
          }}
        >
          <Box
            sx={{
              width: 24,
              flexShrink: 0,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "common.white",
              background: "linear-gradient(90deg, #7ecbe0 0%, #35a3c4 100%)",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                writingMode: "vertical-rl",
                transform: "rotate(180deg)",
                letterSpacing: 3,
                fontWeight: 700,
              }}
            >
              GULF OF MEXICO
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                gap: 0.5,
                justifyContent: "flex-end",
                flexWrap: "wrap",
              }}
            >
              {layout.top.map(renderSpot)}
            </Box>

            <Box sx={{ display: "flex", alignItems: "stretch", my: 0.5 }}>
              {/* The staging area is absolutely positioned so however many
                  names are waiting, they never stretch the box or push the
                  bottom row down — the right column sets the height. */}
              <Box
                sx={{
                  flexGrow: 1,
                  minWidth: 0,
                  mr: { xs: 0.5, sm: 2 },
                  position: "relative",
                }}
              >
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  overflowY: "auto",
                  display: "flex",
                  flexWrap: "wrap",
                  alignContent: "flex-start",
                  justifyContent: "center",
                  gap: 0.75,
                  py: 0.5,
                }}
              >
                {loading ? (
                  <CircularProgress size={24} sx={{ mt: 4 }} />
                ) : unplaced.length === 0 ? (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#c9b68b",
                      letterSpacing: 3,
                      fontWeight: 700,
                      mt: 4,
                    }}
                  >
                    {pool.length ? "ALL SETS PLACED" : "NO BOOKINGS"}
                  </Typography>
                ) : (
                  <>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ width: "100%", textAlign: "center" }}
                    >
                      Drag a name onto a spot — or tap the name, then tap a spot.
                    </Typography>
                    {unplaced.map((entry) => (
                      <Chip
                        key={entry.key}
                        size={small ? "small" : "medium"}
                        draggable
                        onDragStart={(event) => {
                          event.dataTransfer.setData("text/plain", entry.key);
                          setMovingSpot(null);
                          setArmedKey(entry.key);
                        }}
                        onDragEnd={() => setArmedKey(null)}
                        onClick={() => {
                          setMovingSpot(null);
                          setArmedKey((prev) =>
                            prev === entry.key ? null : entry.key,
                          );
                        }}
                        color={armedKey === entry.key ? "secondary" : "default"}
                        variant={armedKey === entry.key ? "filled" : "outlined"}
                        label={`${entry.name} × ${entry.remaining}`}
                        sx={{ cursor: "grab", maxWidth: "100%" }}
                      />
                    ))}
                  </>
                )}
                </Box>
              </Box>

              <Stack spacing={0.5}>{layout.right.map(renderSpot)}</Stack>
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 0.5,
                justifyContent: "flex-end",
                flexWrap: "wrap",
              }}
            >
              {layout.bottom.map(renderSpot)}
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 2,
            justifyContent: "center",
            flexWrap: "wrap",
            mt: 1.5,
          }}
        >
          <LegendSwatch sx={{ bgcolor: "primary.main" }} label="Assigned" />
          <LegendSwatch
            sx={{ border: "1.5px dashed", borderColor: "grey.400" }}
            label="Open"
          />
          <LegendSwatch
            sx={{ border: "1.5px dashed", borderColor: "secondary.light" }}
            label="Named, unassigned"
          />
        </Box>
        {movingSpot != null ? (
          <Alert
            severity="info"
            sx={{ mt: 1.5 }}
            action={
              <Button size="small" onClick={() => setMovingSpot(null)}>
                Cancel
              </Button>
            }
          >
            Moving set #{movingSpot} — tap where it should go. The sets in
            between shift over to make room.
          </Alert>
        ) : (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", textAlign: "center", mt: 1 }}
          >
            Drag a placed set to another spot to slot it in — the sets in
            between shift over. Tap one to rename, move, or free it up.
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button startIcon={<PictureAsPdfIcon />} onClick={handleDownloadPdf}>
          Share Map (PDF)
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      <Dialog open={editingSpot != null} onClose={closeEditor} maxWidth="xs" fullWidth>
        <DialogTitle>Spot #{editingSpot}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {editingAssignment?.bookedBy
              ? `Booked by ${editingAssignment.bookedBy}`
              : "Not assigned to a booking yet."}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Guest name"
            value={aliasDraft}
            onChange={(event) => setAliasDraft(event.target.value)}
            helperText="Optional — who is actually sitting here. Kept if you unassign."
          />
        </DialogContent>
        <DialogActions sx={{ flexWrap: "wrap" }}>
          {editingAssignment?.key && (
            <>
              <Button onClick={handleMoveFromEditor}>Move</Button>
              <Button color="warning" onClick={handleUnassign}>
                Unassign
              </Button>
            </>
          )}
          <Button onClick={closeEditor}>Cancel</Button>
          <Button variant="contained" onClick={saveAlias}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default SpotMapDialog;
